import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, ReactNode } from 'react';

// User preference types
export type UserRole = 'tech' | 'exec' | null;
export type UserIndustry = 'healthcare' | 'fintech' | 'technology' | 'other' | null;

export interface UserPreferences {
  role: UserRole;
  industry: UserIndustry;
  hasCompletedOnboarding: boolean;
  email: string | null;
  emailCapturedAt: string | null;
}

interface UserPreferencesContextType {
  preferences: UserPreferences;
  setRole: (role: UserRole) => void;
  setIndustry: (industry: UserIndustry) => void;
  setEmail: (email: string) => void;
  completeOnboarding: () => void;
  resetPreferences: () => void;
  hasPreferences: boolean;
  hasEmail: boolean;
}

// Local storage key
const PREFERENCES_STORAGE_KEY = 'cybersec-user-preferences';

// Default preferences
const defaultPreferences: UserPreferences = {
  role: null,
  industry: null,
  hasCompletedOnboarding: false,
  email: null,
  emailCapturedAt: null,
};

// Get stored preferences
const getStoredPreferences = (): UserPreferences => {
  if (typeof window === 'undefined') return defaultPreferences;
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        role: parsed.role || null,
        industry: parsed.industry || null,
        hasCompletedOnboarding: parsed.hasCompletedOnboarding || false,
        email: parsed.email || null,
        emailCapturedAt: parsed.emailCapturedAt || null,
      };
    }
  } catch (error) {
    console.warn('Failed to read preferences from localStorage:', error);
  }
  return defaultPreferences;
};

// Store preferences
const storePreferences = (preferences: UserPreferences): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to store preferences:', error);
  }
};

// Create context
const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

// Custom hook to use preferences context
export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

// Provider props
interface UserPreferencesProviderProps {
  children: ReactNode;
}

// Provider component
export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const [preferences, setPreferencesState] = useState<UserPreferences>(() => getStoredPreferences());

  // Persist preferences when they change
  useEffect(() => {
    storePreferences(preferences);
  }, [preferences]);

  const setRole = useCallback((role: UserRole) => {
    setPreferencesState(prev => ({ ...prev, role }));
  }, []);

  const setIndustry = useCallback((industry: UserIndustry) => {
    setPreferencesState(prev => ({ ...prev, industry }));
  }, []);

  const setEmail = useCallback((email: string) => {
    setPreferencesState(prev => ({
      ...prev,
      email,
      emailCapturedAt: new Date().toISOString(),
    }));
  }, []);

  const completeOnboarding = useCallback(() => {
    setPreferencesState(prev => ({ ...prev, hasCompletedOnboarding: true }));
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferencesState(defaultPreferences);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(PREFERENCES_STORAGE_KEY);
    }
  }, []);

  const hasPreferences = preferences.role !== null || preferences.industry !== null;
  const hasEmail = preferences.email !== null;

  const value = useMemo<UserPreferencesContextType>(() => ({
    preferences,
    setRole,
    setIndustry,
    setEmail,
    completeOnboarding,
    resetPreferences,
    hasPreferences,
    hasEmail,
  }), [preferences, setRole, setIndustry, setEmail, completeOnboarding, resetPreferences, hasPreferences, hasEmail]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
